/**
 * Zoom and pan for the 2D plan views (room editor and assembler).
 *
 * The fitted viewport, the whole drawing centered on the stage, is the
 * minimum zoom: nothing zooms out past it, and at that zoom the view is always
 * centered. Zoomed in, the visible window pans freely inside the fitted
 * window, and past its left and bottom edges by up to the minimap's
 * footprint, so every part of the drawing can be panned clear of the minimap
 * in the stage's bottom-left corner and there is little empty paper to get
 * lost in. That allowance grows with the zoom from nothing at the fit, so
 * zooming out glides into the fit without a jump. Every
 * function here is pure over (viewport, fit, stage size); the stages call them
 * from wheel and minimap input and store the result as the state's viewport.
 */
import type { PlanBounds, Point } from '@kaiizen/kai-floor-plan';
import { clamp, modelToScreen, screenToModel, type Viewport } from './viewport';
import { mouseWheelZoomFactor, type WheelDevice } from './wheelInput';

/** How far past the fitted minimum the view may zoom in. */
export const MAX_ZOOM_FACTOR = 12;
const ZOOM_EPSILON = 1e-6;

/** The minimap's distance from the stage's left and bottom edges (px). */
export const MINIMAP_MARGIN = 14;
/** The minimap keeps the stage's shape, as large as fits in this box (px). */
const MINIMAP_MAX_WIDTH = 200, MINIMAP_MAX_HEIGHT = 150;

/** Minimap px per stage px. */
const minimapRatio = (stageW: number, stageH: number): number => Math.min(MINIMAP_MAX_WIDTH / Math.max(1, stageW), MINIMAP_MAX_HEIGHT / Math.max(1, stageH));

/**
 * The stage px the minimap claims from the bottom-left corner while it shows:
 * its size, with its margin on each side.
 */
function minimapFootprint(stageW: number, stageH: number): { width: number; height: number } {
  const ratio = minimapRatio(stageW, stageH);
  return { width: stageW * ratio + 2 * MINIMAP_MARGIN, height: stageH * ratio + 2 * MINIMAP_MARGIN };
}

/**
 * How far past the fitted window's left and bottom edges the window may pan
 * at `scale`: the minimap's footprint, but never more than the fitted window
 * outgrows the stage by. It is nothing at the fit and grows with the zoom, so
 * a view zooming out reaches the fit without a jump.
 */
function minimapAllowance(scale: number, fit: Viewport, stageW: number, stageH: number): { width: number; height: number } {
  const footprint = minimapFootprint(stageW, stageH);
  const growth = scale / fit.scale - 1;
  return { width: Math.min(footprint.width, growth * stageW), height: Math.min(footprint.height, growth * stageH) };
}

/** Is the view zoomed in past the fitted minimum? */
export const isZoomedIn = (viewport: Viewport, fit: Viewport): boolean => viewport.scale > fit.scale * (1 + ZOOM_EPSILON);

/** The model-space rectangle a viewport shows on a stage of the given size. */
export function viewportWindow(viewport: Viewport, stageW: number, stageH: number): PlanBounds {
  const topLeft = screenToModel(viewport, { x: 0, y: 0 }), bottomRight = screenToModel(viewport, { x: stageW, y: stageH });
  return { minX: topLeft.x, minY: topLeft.y, maxX: bottomRight.x, maxY: bottomRight.y };
}

/**
 * Hold a viewport inside the zoom/pan envelope: at (or below) the fitted
 * minimum it IS the fit; zoomed in, its scale is capped and its offset clamped
 * so the visible window stays inside the fitted window, grown at its left and
 * bottom by the minimap's allowance at that zoom. Returns `viewport` itself
 * when nothing had to change.
 */
export function constrainViewport(viewport: Viewport, fit: Viewport, stageW: number, stageH: number): Viewport {
  if (!isZoomedIn(viewport, fit)) return fit;
  const scale = Math.min(viewport.scale, fit.scale * MAX_ZOOM_FACTOR);
  const world = viewportWindow(fit, stageW, stageH);
  const minimap = minimapAllowance(scale, fit, stageW, stageH);
  const offset = {
    x: clamp(viewport.offset.x, stageW - world.maxX * scale, minimap.width - world.minX * scale),
    y: clamp(viewport.offset.y, stageH - minimap.height - world.maxY * scale, -world.minY * scale),
  };
  return scale === viewport.scale && offset.x === viewport.offset.x && offset.y === viewport.offset.y ? viewport : { scale, offset };
}

/** Zoom by `factor` keeping the model point under `anchor` (stage px) fixed, within the envelope. */
export function zoomViewportAt(viewport: Viewport, fit: Viewport, factor: number, anchor: Point, stageW: number, stageH: number): Viewport {
  const scale = clamp(viewport.scale * factor, fit.scale, fit.scale * MAX_ZOOM_FACTOR);
  const model = screenToModel(viewport, anchor);
  return constrainViewport({ scale, offset: { x: anchor.x - model.x * scale, y: anchor.y - model.y * scale } }, fit, stageW, stageH);
}

/** Pan by a screen delta, within the envelope. */
export function panViewport(viewport: Viewport, fit: Viewport, dx: number, dy: number, stageW: number, stageH: number): Viewport {
  return constrainViewport({ scale: viewport.scale, offset: { x: viewport.offset.x + dx, y: viewport.offset.y + dy } }, fit, stageW, stageH);
}

/** Center the view on a model point (the minimap's click and drag), within the envelope. */
export function centerViewportOn(viewport: Viewport, fit: Viewport, center: Point, stageW: number, stageH: number): Viewport {
  return constrainViewport(
    { scale: viewport.scale, offset: { x: stageW / 2 - center.x * viewport.scale, y: stageH / 2 - center.y * viewport.scale } },
    fit,
    stageW,
    stageH,
  );
}

/** The parts of a wheel event the view reads. */
export interface WheelGesture {
  deltaX: number;
  deltaY: number;
  /** 0 = pixels, 1 = lines, 2 = pages (WheelEvent.deltaMode). */
  deltaMode?: number;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  /** What sent it (see createWheelClassifier); a mouse wheel zooms. */
  device?: WheelDevice;
}

/**
 * One wheel event → the next viewport. A pinch (the trackpad reports it as a
 * ctrl-wheel), a modifier-held wheel or a mouse wheel zooms about the pointer;
 * shift turns a mouse wheel into a sideways pan. A two-finger swipe pans
 * while zoomed in; at the fitted minimum there is nothing to pan, so it zooms
 * in instead (and never out).
 */
export function applyWheelToViewport(viewport: Viewport, fit: Viewport, wheel: WheelGesture, anchor: Point, stageW: number, stageH: number): Viewport {
  const unit = wheel.deltaMode === 1 ? 16 : wheel.deltaMode === 2 ? stageH : 1;
  const dx = wheel.deltaX * unit, dy = wheel.deltaY * unit;
  if (wheel.device === 'mouse') {
    // Shift turns the wheel sideways (macOS already reports it as deltaX).
    if (wheel.shiftKey) return panViewport(viewport, fit, -(dx || dy), 0, stageW, stageH);
    return zoomViewportAt(viewport, fit, mouseWheelZoomFactor(wheel), anchor, stageW, stageH);
  }
  const zoomGesture = !!(wheel.ctrlKey || wheel.metaKey);
  if (zoomGesture || !isZoomedIn(viewport, fit)) {
    // Pinch deltas are a few px per event; a mouse-wheel notch is ~100. The
    // clamp keeps one notch to a comfortable ~1.8× step.
    const factor = Math.exp(-clamp(dy, -60, 60) * 0.01);
    if (!zoomGesture && factor <= 1) return viewport;
    return zoomViewportAt(viewport, fit, factor, anchor, stageW, stageH);
  }
  return panViewport(viewport, fit, -dx, -dy, stageW, stageH);
}

/**
 * Pan just enough that `bounds` (model space) is on screen with `marginPx` of
 * breathing room, or, when it is too big for the window, center on it. A
 * bounds already in view leaves the viewport untouched (same object).
 */
export function revealInViewport(viewport: Viewport, fit: Viewport, bounds: PlanBounds, stageW: number, stageH: number, marginPx = 40): Viewport {
  const topLeft = modelToScreen(viewport, { x: bounds.minX, y: bounds.minY });
  const bottomRight = modelToScreen(viewport, { x: bounds.maxX, y: bounds.maxY });
  const shift = (low: number, high: number, extent: number): number => {
    if (high - low > extent - 2 * marginPx) return extent / 2 - (low + high) / 2; // too big: center on it
    if (low < marginPx) return marginPx - low;
    if (high > extent - marginPx) return extent - marginPx - high;
    return 0;
  };
  const dx = shift(topLeft.x, bottomRight.x, stageW), dy = shift(topLeft.y, bottomRight.y, stageH);
  return dx || dy ? panViewport(viewport, fit, dx, dy, stageW, stageH) : viewport;
}

/**
 * The viewport after the drawing's outer boundary changed (its fit moved from
 * `previousFit` to `fit`): a view that sat at the old fit sits at the new one,
 * re-centered; a zoomed-in view keeps its zoom, is held inside the new fitted
 * window, and pans just enough to show `reveal` (what changed) when given.
 */
export function refitViewport(viewport: Viewport, previousFit: Viewport, fit: Viewport, stageW: number, stageH: number, reveal?: PlanBounds | null): Viewport {
  if (!isZoomedIn(viewport, previousFit)) return fit;
  const constrained = constrainViewport(viewport, fit, stageW, stageH);
  return reveal ? revealInViewport(constrained, fit, reveal, stageW, stageH) : constrained;
}

/** The minimap: the fitted view shrunk to a thumbnail, and the current window's rectangle on it. */
export interface MinimapGeometry {
  width: number;
  height: number;
  /** Projects model points onto the thumbnail. */
  viewport: Viewport;
  /** The current visible window, in thumbnail px. */
  window: { x: number; y: number; width: number; height: number };
}

export function minimapGeometry(fit: Viewport, viewport: Viewport, stageW: number, stageH: number): MinimapGeometry {
  const ratio = minimapRatio(stageW, stageH);
  const width = stageW * ratio, height = stageH * ratio;
  const thumbnail = { scale: fit.scale * ratio, offset: { x: fit.offset.x * ratio, y: fit.offset.y * ratio } };
  const visible = viewportWindow(viewport, stageW, stageH);
  const topLeft = modelToScreen(thumbnail, { x: visible.minX, y: visible.minY });
  const bottomRight = modelToScreen(thumbnail, { x: visible.maxX, y: visible.maxY });
  return { width, height, viewport: thumbnail, window: { x: topLeft.x, y: topLeft.y, width: bottomRight.x - topLeft.x, height: bottomRight.y - topLeft.y } };
}
